import React, { memo, useRef } from 'react';
import { mergeProps, useFocusVisible, useHover, useTab } from 'react-aria';
import { Node } from '@react-types/shared';
import { mergeRefs } from '@direct-frontend/stdlib';

import { tabMenuCls } from '../utils';
import { TabMenuItem } from '../types';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import { useTypographyWithSkin } from '../../../hooks/useTypography/useTypographyWithSkin';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { useHStack } from '../../../hooks/useStack';

import { TabProps } from './types';

type TabItemRendererProps = {
    item: TabMenuItem;
};

const TabItemRenderer = ({ item }: TabItemRendererProps) => {
    return <>{item.title}</>;
};

// Специальная функция, используемая в коллекциях react-aria,
// возвращает в том числе rendered — отображаемый контент вкладки
TabItemRenderer.getCollectionNode = function* (props: TabItemRendererProps): Generator<Partial<Node<TabMenuItem>>> {
    const { item } = props;

    yield {
        type: 'item',
        props: item,
        rendered: typeof item.title === 'function' ? item.title({}) : item.title,
        hasChildNodes: false,
        key: item.key,
    };
};

const entryRenderer = (item: TabMenuItem) => {
    return <TabItemRenderer item={item} />;
};

export function getDefaultTabRenderer() {
    return entryRenderer;
}

/**
 * Компонент для отображения названия вкладки
 */
export const Tab = memo<TabProps>(function Tab({ item, size, state, activeTabRef, ...props }) {
    const { key, disabled, title, href, addonRight } = item;

    const ref = useRef(null);
    const { tabProps } = useTab({ key: key, isDisabled: disabled }, state, ref);

    const isSelected = state.selectedKey === key;
    const isDisabled = state.disabledKeys.has(key);
    const isFocused = state.selectionManager.isFocused && state.selectionManager.focusedKey === key;

    const ElementType = !href || disabled ? 'li' : 'a';

    const { hoverProps, isHovered } = useHover({ isDisabled });
    const { isFocusVisible } = useFocusVisible();
    const sizeSkinClass = getSizeSkin(size);
    const colorSkinClass = getColorSkin('gray-transparent', { isDisabled });

    const { textClasses } = useTypographyWithSkin({
        size: 'current-main',
        bold: true,
        color: 'current',
        role: isSelected || isHovered ? 'main' : 'supplementary',
    });

    const hStackClasses = useHStack({ alignItems: 'center', justifyContent: 'center', gap: '8' });

    const addonRightNode =
        addonRight && typeof addonRight === 'function'
            ? addonRight({ role: isSelected ? 'main' : 'supplementary' })
            : addonRight;

    const itemProps = {
        ...mergeProps(tabProps, hoverProps),

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        ref: isSelected ? mergeRefs(ref, activeTabRef) : ref,
        className: tabMenuCls(
            'tab',
            {
                size,
                active: isSelected,
                focused: isFocused && isFocusVisible,
                hovered: isHovered,
                disabled: isDisabled,
                link: Boolean(href),
            },
            [textClasses, sizeSkinClass, colorSkinClass, hStackClasses],
        ),
        'data-testid': props['data-testid'],
    };

    return typeof title === 'function' ? (
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        title({ ...itemProps, ...(addonRight && { addonRightNode }) })
    ) : (
        <ElementType {...itemProps} title={title}>
            {title}
            {addonRightNode}
        </ElementType>
    );
});
